import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { FilePen } from 'lucide-react'
import { CORRECTION_STATUSES, type CorrectionStatus } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  Empty,
  MatriculeTag,
  Modal,
  PageHeader,
  SegmentedPills,
  Textarea,
  useAuth,
  useToast,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import type { Paginated } from '../../lib/rbac-types'
import { CORRECTION_VARIANT, CorrectionList, type Correction } from './ApplicationPage'
import { APPLICATIONS_KEY } from './ApplicationsPage'

const KEY = ['admin', 'corrections'] as const

type Row = Correction & {
  objectId: string
  applicationNumber: string | null
  competition: { reference: string; title: string }
  mine: boolean
}

/**
 * Corrections après dépôt (ADR-013) : file des demandes à valider. 🔒 Le
 * demandeur ne valide jamais sa propre demande (contrôlé par l'API et la base).
 */
export function CorrectionsPage() {
  const { session } = useAuth()
  const [status, setStatus] = useState<CorrectionStatus>('pending')
  const [deciding, setDeciding] = useState<Row | null>(null)
  const list = useQuery({
    queryKey: [...KEY, status],
    queryFn: () =>
      session.api.get<
        Paginated<Row> & { meta: { byStatus: Partial<Record<CorrectionStatus, number>> } }
      >('/admin/corrections', { query: { status } }),
    placeholderData: keepPreviousData,
  })
  const counts = list.data?.meta.byStatus ?? {}

  return (
    <>
      <PageHeader
        eyebrow="Dossiers"
        title="Corrections après dépôt"
        description="Chaque correction d’un dossier déposé est motivée par son auteur puis validée par une autre personne habilitée. La version déposée reste toujours consultable."
      />
      <Card>
        <div className="filter-row">
          <SegmentedPills
            label="Statut"
            value={status}
            onChange={setStatus}
            options={(Object.keys(CORRECTION_STATUSES) as CorrectionStatus[]).map((s) => ({
              value: s,
              label: `${CORRECTION_STATUSES[s]} · ${counts[s] ?? 0}`,
            }))}
          />
        </div>
        {list.isError && <Alert tone="danger">{errorMessage(list.error)}</Alert>}
        <CardBody>
          {list.data?.data.length ? (
            <ul className="application-list">
              {list.data.data.map((row) => (
                <li key={row.id}>
                  <div className="grow stack" style={{ gap: 'var(--space-2)' }}>
                    <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      {row.applicationNumber && (
                        <MatriculeTag
                          kind="candidature"
                          value={row.applicationNumber}
                          copyable={false}
                        />
                      )}
                      <Badge variant={CORRECTION_VARIANT[row.status]}>
                        {CORRECTION_STATUSES[row.status]}
                      </Badge>
                      {row.mine && <Badge variant="outline">Ma demande</Badge>}
                      <span className="text-muted" style={{ fontSize: 'var(--text-sm)' }}>
                        {row.competition.reference} · {when(row.requestedAt)}
                      </span>
                    </div>
                    <CorrectionList corrections={[row]} />
                  </div>
                  <div className="stack" style={{ gap: 'var(--space-2)' }}>
                    <Link className="btn btn-secondary btn-sm" to={`/candidatures/${row.objectId}`}>
                      Voir le dossier
                    </Link>
                    {row.status === 'pending' && !row.mine && (
                      <Can perm="corrections.approve">
                        <Button size="sm" variant="primary" onClick={() => setDeciding(row)}>
                          Examiner
                        </Button>
                      </Can>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : list.isPending ? (
            <div className="skeleton" style={{ height: 120 }} />
          ) : (
            <Empty
              icon={<FilePen aria-hidden="true" />}
              title="Aucune demande dans cette catégorie"
            />
          )}
        </CardBody>
      </Card>
      {deciding && <DecisionModal row={deciding} onClose={() => setDeciding(null)} />}
    </>
  )
}

function DecisionModal({ row, onClose }: { row: Row; onClose: () => void }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')
  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      session.api.post(`/admin/corrections/${row.id}/decision`, { decision, note }),
    onSuccess: async (_, decision) => {
      toast.success(
        decision === 'approved' ? 'Correction validée et appliquée' : 'Correction refusée'
      )
      await queryClient.invalidateQueries({ queryKey: KEY })
      await queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY })
      onClose()
    },
    onError: (error) => {
      if (!fieldErrors(error).note) toast.error(errorMessage(error))
    },
  })
  const ok = note.trim().length >= 10
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Examiner la demande de correction"
      description={`Dossier ${row.applicationNumber ?? ''} — demandée par ${row.requestedBy.label ?? '—'}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Plus tard
          </Button>
          <Button
            variant="danger"
            disabled={!ok}
            loading={decide.isPending && decide.variables === 'rejected'}
            onClick={() => decide.mutate('rejected')}
          >
            Refuser
          </Button>
          <Button
            variant="primary"
            disabled={!ok}
            loading={decide.isPending && decide.variables === 'approved'}
            onClick={() => decide.mutate('approved')}
          >
            Valider et appliquer
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        <CorrectionList corrections={[row]} />
        <Textarea
          label="Note de décision"
          required
          rows={3}
          hint="10 caractères minimum (vérification effectuée, pièce consultée…)."
          value={note}
          error={fieldErrors(decide.error).note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
    </Modal>
  )
}
